import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading" });
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/api/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid" });
          return;
        }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        if (attempts >= 10) { setState({ status: "processing" }); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 10) { setState({ status: "processing" }); return; }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="container-jcc py-24 max-w-xl" data-testid="page-checkout-success">
      {state.status === "loading" && (
        <div className="text-center" data-testid="checkout-loading">
          <Clock className="w-10 h-10 mx-auto text-teal animate-pulse" />
          <h1 className="mt-4 font-display text-3xl font-bold">Confirming your payment…</h1>
          <p className="mt-2 text-ink-muted">This usually takes a few seconds.</p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="text-center" data-testid="checkout-paid">
          <CheckCircle2 className="w-12 h-12 mx-auto text-teal" />
          <h1 className="mt-4 font-display text-3xl font-bold">You're in.</h1>
          <p className="mt-2 text-ink-muted">Your membership is active. Everything is unlocked.</p>
          <Link
            to="/dashboard"
            className="btn-teal mt-6 inline-flex"
            data-testid="checkout-paid-dashboard-link"
          >
            Go to dashboard
          </Link>
        </div>
      )}
      {state.status === "processing" && (
        <div className="text-center" data-testid="checkout-processing">
          <Mail className="w-12 h-12 mx-auto text-teal" />
          <h1 className="mt-4 font-display text-3xl font-bold">Payment received — finalising.</h1>
          <p className="mt-3 text-ink-muted">
            Thanks. Your payment has been received and is being confirmed by our payment provider. This usually takes under a minute. We'll send you an email and your dashboard will update automatically — no need to refresh anything urgently.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/dashboard"
              className="btn-teal"
              data-testid="checkout-processing-dashboard-link"
            >
              Go to dashboard
            </Link>
            <Link to="/contact" className="btn-outline">Contact us</Link>
          </div>
        </div>
      )}
      {state.status === "expired" && (
        <div className="text-center" data-testid="checkout-expired">
          <XCircle className="w-12 h-12 mx-auto text-amber-500" />
          <h1 className="mt-4 font-display text-3xl font-bold">Checkout session expired.</h1>
          <p className="mt-2 text-ink-muted">No charge was made. You can start a new checkout whenever you're ready.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/pro-carer-circle" className="btn-purple">Pro Carer Office</Link>
            <Link to="/family-access" className="btn-teal">Family Care Office</Link>
          </div>
        </div>
      )}
      {state.status === "error" && (
        <div className="text-center" data-testid="checkout-error">
          <XCircle className="w-12 h-12 mx-auto text-red-600" />
          <h1 className="mt-4 font-display text-3xl font-bold">We couldn't find that session.</h1>
          <p className="mt-2 text-ink-muted">Please contact us if you've been charged but don't see your membership active.</p>
          <Link to="/contact" className="btn-outline mt-6 inline-flex">Contact us</Link>
        </div>
      )}
    </div>
  );
}
